import { useEffect, useState } from 'react';
import type { MediaTranscodeProgress, PlaybackSource, WarpPreset, WarpPresetV2 } from '@cornerstone/shared';
import { useCalibrationStore, type ConnectionStatus, type MediaChangePayload } from '../store/calibration';
import { useCalibrationV2Store } from '../store/calibrationV2';
import {
  useLibraryStore,
  type NoticeChangePayload,
  type PlaylistChangePayload,
  type ScheduleChangePayload,
  type ScheduleTypeChangePayload,
  type TimelineBlockChangePayload,
} from '../store/library';
import { API_BASE, WS_URL } from './api';

// =============================================================================
// WebSocket client — server-push events (CALIBRATION_UX §6.2).
// Auto-reconnects with a 2s backoff. Exposes `connectionStatus` via store.
// =============================================================================

type ServerStateSnapshot = {
  playbackState: 'playing' | 'paused' | 'stopped';
  playbackMode: 'schedule' | 'manual';
  playbackSource?: PlaybackSource;
};

export type WsEvent =
  | { type: 'scope:changed'; payload: Record<string, never> }
  | { type: 'workflow:changed'; payload: Record<string, never> }
  | { type: 'state:update'; payload: ServerStateSnapshot }
  | { type: 'warp:update'; payload: unknown }
  | { type: 'warp:saved'; payload: WarpPreset }
  | { type: 'warp:deleted'; payload: { id: string } }
  // Calibration v2 — 기존 warp 이벤트의 V2-suffix 병행 채널 (P2 계약).
  | { type: 'warp:savedV2'; payload: WarpPresetV2 }
  | { type: 'warp:deletedV2'; payload: { id: string } }
  | { type: 'media:change'; payload: MediaChangePayload }
  | { type: 'media:progress'; payload: MediaTranscodeProgress }
  | { type: 'playlist:change'; payload: PlaylistChangePayload }
  | { type: 'schedule:change'; payload: ScheduleChangePayload }
  | { type: 'schedule:fired'; payload: { id: string; name: string; at: string } }
  | { type: 'schedule-type:change'; payload: ScheduleTypeChangePayload }
  | { type: 'timeline-block:change'; payload: TimelineBlockChangePayload }
  | { type: 'test-source:change'; payload: unknown }
  | { type: 'notice:change'; payload: NoticeChangePayload }
  | { type: 'playback:progress'; payload: { position_sec: number } }
  | { type: 'log:entry'; payload: unknown };

// GNB 서버 상태(BRICK-01~03) — 전송 계층(WS)과 의존성 상태(/health/ready)를
// 합성한 3값. WS만으로는 "서버는 살아 있으나 DB/스토리지가 죽은" 상태를 구분할
// 수 없어 ready 프로브를 겹친다.
//   ok    = WS online + ready 200  → 정상
//   error = WS online + ready 503  → 오류
//   down  = WS connecting/offline  → 연결끊김
export type ServerStatus = 'ok' | 'error' | 'down';

const READY_PROBE_MS = 15_000;

export function deriveServerStatus(connection: ConnectionStatus, ready: boolean): ServerStatus {
  if (connection !== 'online') return 'down';
  return ready ? 'ok' : 'error';
}

export function useServerStatus(): ServerStatus {
  const connection = useCalibrationStore((s) => s.connectionStatus);
  const [ready, setReady] = useState(true);

  useEffect(() => {
    if (connection !== 'online') return;
    let alive = true;
    const probe = (): void => {
      fetch(`${API_BASE}/health/ready`)
        .then((r) => {
          if (alive) setReady(r.ok);
        })
        .catch(() => {
          if (alive) setReady(false);
        });
    };
    probe();
    const timer = window.setInterval(probe, READY_PROBE_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [connection]);

  return deriveServerStatus(connection, ready);
}

// 앱 전체에서 한 번만 호출한다 — 호출마다 소켓이 하나씩 열리므로 페이지
// 컴포넌트에서 부르면 중복 연결이 생긴다. AppShell 최상단에서 마운트한다.
export function useServerConnection(): void {
  const setConnectionStatus = useCalibrationStore((s) => s.setConnectionStatus);
  const setConnectionStatusV2 = useCalibrationV2Store((s) => s.setConnectionStatus);
  const receivePresetSavedV2 = useCalibrationV2Store((s) => s.receivePresetSaved);
  const receivePresetDeletedV2 = useCalibrationV2Store((s) => s.receivePresetDeleted);
  const receivePresetSaved = useCalibrationStore((s) => s.receivePresetSaved);
  const receivePresetDeleted = useCalibrationStore((s) => s.receivePresetDeleted);
  const receiveMediaChange = useCalibrationStore((s) => s.receiveMediaChange);
  const receiveMediaProgress = useCalibrationStore((s) => s.receiveMediaProgress);
  const setPlaybackState = useCalibrationStore((s) => s.setPlaybackState);
  const setPlaybackMode = useCalibrationStore((s) => s.setPlaybackMode);
  const setPlaybackSource = useCalibrationStore((s) => s.setPlaybackSource);
  const receivePlaylistChange = useLibraryStore((s) => s.receivePlaylistChange);
  const receiveScheduleChange = useLibraryStore((s) => s.receiveScheduleChange);
  const receiveScheduleTypeChange = useLibraryStore((s) => s.receiveScheduleTypeChange);
  const receiveTimelineBlockChange = useLibraryStore((s) => s.receiveTimelineBlockChange);
  const receiveNoticeChange = useLibraryStore((s) => s.receiveNoticeChange);

  useEffect(() => {
    let closed = false;
    let retry: number | null = null;
    let ws: WebSocket | null = null;
    let refreshTimer: number | null = null;
    const reloadLibrary = () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        const library = useLibraryStore.getState();
        void Promise.allSettled([useCalibrationStore.getState().loadMedia(), library.loadPlaylists(), library.loadNotices(),
          library.loadSchedules(), library.loadScheduleTypes(), library.loadTimelineBlocks(), library.loadFolders()]);
      }, 150);
    };
    /** 권한·소유 구조가 바뀌었다 — 보던 것을 감추고 권한부터 다시 확인한다. */
    const refreshScope = () => {
      window.dispatchEvent(new Event('bricklit:scope-changed'));
      reloadLibrary();
    };
    /**
     * 편성·승인·콘텐츠가 바뀌었다. 예전에는 이것도 scope-changed로 흘려보내서, 다른 관리자가
     * 무엇 하나 저장할 때마다 보고 있던 사람의 기기 목록·기기 상세·권한 배지가 통째로 비었다가
     * 다시 채워졌다(2026-09-17 대교 "다른 관리자가 변경하면 내 화면이 꿈뻑거린다").
     * 권한은 그대로이므로 감추지 않고 데이터만 다시 읽는다.
     */
    const refreshWorkflow = () => {
      window.dispatchEvent(new Event('bricklit:workflow-changed'));
      reloadLibrary();
    };

    const open = () => {
      if (closed) return;
      setConnectionStatus('connecting');
      setConnectionStatusV2('connecting');
      ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        if (closed) return;
        setConnectionStatus('online');
        setConnectionStatusV2('online');
      };
      ws.onclose = (event) => {
        if (closed) return;
        if (event.code === 1008) {
          // Discard component drafts and in-flight responses after an
          // authorization change, not only the current store contents.
          closed = true;
          window.dispatchEvent(new Event('bricklit:authorization-reset'));
          useLibraryStore.setState({ playlists: [], notices: [], schedules: [], scheduleTypes: [], timelineBlocks: [], folders: [] });
          useCalibrationStore.setState({ mediaList: [], recentMedia: [], currentMedia: null });
          if (refreshTimer !== null) window.clearTimeout(refreshTimer);
          setConnectionStatus('offline');
          setConnectionStatusV2('offline');
          window.location.reload();
          return;
        }
        setConnectionStatus('offline');
        setConnectionStatusV2('offline');
        if (!closed) {
          retry = window.setTimeout(open, 2000);
        }
      };
      ws.onerror = () => {
        // onclose will run after
      };
      ws.onmessage = (e) => {
        if (closed) return;
        try {
          const data = JSON.parse(e.data as string) as WsEvent;
          switch (data.type) {
            case 'scope:changed':
              refreshScope();
              break;
            case 'workflow:changed':
              refreshWorkflow();
              break;
            case 'warp:saved':
              receivePresetSaved(data.payload);
              break;
            case 'warp:deleted':
              receivePresetDeleted(data.payload.id);
              break;
            case 'warp:savedV2':
              receivePresetSavedV2(data.payload);
              break;
            case 'warp:deletedV2':
              receivePresetDeletedV2(data.payload.id);
              break;
            case 'media:change':
              receiveMediaChange(data.payload);
              break;
            case 'media:progress':
              receiveMediaProgress(data.payload);
              break;
            case 'state:update':
              if (data.payload && typeof data.payload === 'object') {
                setPlaybackState(data.payload.playbackState);
                if (data.payload.playbackMode) {
                  setPlaybackMode(data.payload.playbackMode);
                }
                if (data.payload.playbackSource) {
                  setPlaybackSource(data.payload.playbackSource);
                }
              }
              break;
            case 'playlist:change':
              receivePlaylistChange(data.payload);
              break;
            case 'schedule:change':
              receiveScheduleChange(data.payload);
              break;
            case 'schedule-type:change':
              receiveScheduleTypeChange(data.payload);
              break;
            case 'timeline-block:change':
              receiveTimelineBlockChange(data.payload);
              break;
            case 'notice:change':
              receiveNoticeChange(data.payload);
              break;
            // warp:update / test-source:change / schedule:fired informational
            default:
              break;
          }
        } catch {
          /* ignore malformed frames */
        }
      };
    };

    open();
    return () => {
      closed = true;
      if (retry !== null) window.clearTimeout(retry);
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      ws?.close();
    };
  }, [
    setConnectionStatus,
    setConnectionStatusV2,
    receivePresetSaved,
    receivePresetDeleted,
    receivePresetSavedV2,
    receivePresetDeletedV2,
    receiveMediaChange,
    receiveMediaProgress,
    setPlaybackState,
    setPlaybackMode,
    setPlaybackSource,
    receivePlaylistChange,
    receiveScheduleChange,
    receiveScheduleTypeChange,
    receiveTimelineBlockChange,
    receiveNoticeChange,
  ]);
}
